// src/customHooks/Services/useServices.js (Assuming this path for the custom hook)

import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
// import usePermission from "../../utils/usePermission"; // commented out as it's commented in the original

import { apiHandler } from "../../api";
import { saveStoreData } from "../../features/login/LoginSlice";

const PAGE_SIZE = 100;
const DEBOUNCE_DELAY = 300;
const MIN_SEARCH_LENGTH = 3;

export const useServices = (service = "IGM") => {
 // default to IGM
 const { authData, storeId, raiseIssue } = useSelector(
  (state) => state.loginSlice
 );
 const brandName =
  useSelector((state) => state.loginSlice.brandName) ||
  localStorage.getItem("brandName");

 // stores will hold the grouped data object, not a flat array
 const [stores, setStores] = useState({});
 const [loading, setLoading] = useState(false);
 const [error, setError] = useState(null);
 const [totalCount, setTotalCount] = useState(0);
 const [search, setSearch] = useState("");
 const [initiallyHadData, setInitiallyHadData] = useState(false);
 const dispatch = useDispatch();
 const navigate = useNavigate();
 const debounceTimer = useRef(null);

 // const canRead = usePermission("MULTI_STORES"); // commented out as it's commented in the original

 const fetchStores = async (searchQuery = "") => {
  try {
   setLoading(true);
   setError(null);

   const response = await apiHandler({
    url: `https://identitybackend.ondchub.com/api/v1/tenant/getServiceTenants?iPageSize=${PAGE_SIZE}&sSearchByName=${encodeURIComponent(
     searchQuery
    )}&sService=${encodeURIComponent(service)}`,
    method: "GET",
    authToken: authData,
    dispatch,
    storeId,
   });

   //console.log("Stores API response:", response);

   if (response?.data?.statusCode === 200) {
    // Keep the grouped object structure
    const groupedStoreData = response?.data?.data || {};
    const totalRecords = response?.data?.totalRecords;

    setStores(groupedStoreData); // Save the grouped object
    setError(null);

    if (totalRecords > 0 && !initiallyHadData) {
     setInitiallyHadData(true);
    }

    setTotalCount(totalRecords);
   } else {
    setError(response?.data?.message || "Failed to fetch stores");
   }
  } catch (err) {
   setError(err.message || "Failed to fetch stores");
  } finally {
   setLoading(false);
  }
 };

 const debouncedSearch = useCallback(
  (searchQuery) => {
   if (debounceTimer.current) clearTimeout(debounceTimer.current);

   if (searchQuery === "" || searchQuery.length >= MIN_SEARCH_LENGTH) {
    debounceTimer.current = setTimeout(() => {
     fetchStores(searchQuery);
    }, DEBOUNCE_DELAY);
   }
  },
  [service]
 );

 useEffect(() => {
  fetchStores(search);
 }, [search, service]);

 const handleStoreSearch = (e) => {
  const searchQuery = e.target.value;
  setSearch(searchQuery);
  debouncedSearch(searchQuery);
 };

 const handleChangeStore = (store) => {
  dispatch(
   saveStoreData({
    storeId: store.id,
    brandName: store.brand_name,
    apiKey: store.api_key, // Added apiKey based on first useServices example
   })
  );

  // Use the raiseIssue value to determine the correct path
  const targetPath = raiseIssue ? "/issues/seller" : "/issues/buyer";
  navigate(targetPath, { replace: true });
 };

 return {
  stores, // Now returns the grouped object
  setStores,
  loading,
  setLoading,
  error,
  totalCount,
  handleStoreSearch,
  initiallyHadData,
  search,
  authData,
  handleChangeStore,
 };
};